<script lang="ts">
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import type { Device } from 'src/types/Device';
	import type { ProvisionResult } from 'src/components/Camera/helpers/encryption/types/master-key';
	import {
		camera_setup_prompt,
		shouldShowSetupPrompt,
	} from 'src/components/Camera/stores/camera_setup_prompt';
	import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
	import { devices } from 'src/stores/lists/devices';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { homes } from 'src/stores/account/home';
	import { getCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
	import {
		CAMERA_CLOUD_STORAGE_SCROLL_PARAM,
		getSlotCountsFromSettings,
		hasCameraCloudStorage,
		isCameraAssignableToCloudSlot,
		isHighestCameraCloudStorageTier,
		setCameraSlotList,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import { CameraCloudStorageSubscriptionStatus } from 'src/types/CameraCloudStorage';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import CameraSlotAssignmentPreview from 'src/components/Settings/CameraCloudStorage/CameraSlotAssignmentPreview.svelte';
	import ManageCameraCloudStorageModal from 'src/components/Settings/CameraCloudStorage/ManageCameraCloudStorageModal.svelte';
	import { provisionMasterKey } from 'src/components/Camera/helpers/encryption/master-key-provisioning';
	import { PBKDF2_ITERATIONS } from 'src/components/Camera/helpers/encryption/master-key-management';
	import { base64ToBytes, bytesToBase64 } from 'src/components/Camera/helpers/encryption/encoding';
	import { real_id } from 'src/helpers/interface_helpers';
	import { copy } from 'src/helpers/copy_text';
	import {
		Alert,
		ButtonV2,
		CircleAvatar,
		IconButton,
		Input,
		Modal,
		Text,
		toast,
		Wizard,
		WizardItem,
	} from '@Shelly/shelly-design-system/index';

	export let device: Device;

	/** Minimum passphrase length enforced before provisioning a key. */
	const MIN_PASSPHRASE_LENGTH = 8;

	type SetupNextStep = 'premium' | 'camera-plan' | 'camera-upgrade' | 'assign' | 'manage';

	let passphrase = '';
	let confirmPassphrase = '';
	let passphraseError = '';
	let provisionLoading = false;
	let assignmentLoading = false;
	let currentStep: 1 | 2 = 1;
	let nextStep: SetupNextStep | null = null;
	let showManageModal = false;

	let provisionResult: ProvisionResult | null = null;
	let showKeyModal = false;
	let showDebug = false;

	$: deviceId = real_id(device?.id);
	$: showPassphraseModal = shouldShowSetupPrompt($camera_setup_prompt, device);
	$: isSharedHome = isActiveHomeShared($homes.list);
	$: activeCameraPlan = $camera_cloud_storage.activePlan;
	$: slotCounts = getSlotCountsFromSettings($user_settings);
	$: assignedCameraIds = Object.values($camera_cloud_storage.slotAssignments).filter(Boolean);
	$: availableSlotCount = Math.max(slotCounts.free, slotCounts.all - assignedCameraIds.length, 0);
	$: nextEmptySlotIndex = Array.from({ length: slotCounts.all }, (_, index) => index).find(
		(index) => !$camera_cloud_storage.slotAssignments[index],
	);

	$: if (!showPassphraseModal && currentStep === 2) {
		currentStep = 1;
		nextStep = null;
	}

	const benefits = [
		{
			icon: 'fas fa-clock-rotate-left',
			titleKey: 'activate_storage_benefit_history_title',
			titleDefault: '30 days of video and image history',
			descKey: 'activate_storage_benefit_history_desc',
			descDefault: 'Browse and review up to 30 days of clips and snapshots on demand.',
		},
		{
			icon: 'fas fa-bell-on',
			titleKey: 'activate_storage_benefit_motion_title',
			titleDefault: 'Motion events with recording',
			descKey: 'activate_storage_benefit_motion_desc',
			descDefault:
				'Get the full picture when it matters most, with video backing your key motion events.',
		},
		{
			icon: 'fas fa-shield-check',
			titleKey: 'activate_storage_benefit_ai_title',
			titleDefault: 'AI object recognition',
			descKey: 'activate_storage_benefit_ai_desc',
			descDefault: 'Turn the detected person, pet, or vehicles into smarter Scenes automation.',
		},
	];

	const stepTwoContent: Record<
		SetupNextStep,
		{
			tone: 'premium' | 'camera' | 'ready';
			icon: string;
			eyebrowKey: string;
			eyebrowDefault: string;
			titleKey: string;
			titleDefault: string;
			descriptionKey: string;
			descriptionDefault: string;
			ctaKey: string;
			ctaDefault: string;
		}
	> = {
		premium: {
			tone: 'premium',
			icon: 'fas fa-gem',
			eyebrowKey: 'activate_storage_premium_label',
			eyebrowDefault: 'Recommended for one camera',
			titleKey: 'activate_storage_premium_title',
			titleDefault: 'Upgrade this camera with Premium',
			descriptionKey: 'activate_storage_premium_desc',
			descriptionDefault:
				'Premium includes Camera Cloud Storage for one camera, plus smarter automation, deeper insights, and more powerful home controls.',
			ctaKey: 'button_explore_premium',
			ctaDefault: 'Explore Premium',
		},
		'camera-plan': {
			tone: 'camera',
			icon: 'fas fa-camera',
			eyebrowKey: 'activate_storage_multi_camera_label',
			eyebrowDefault: 'Built for Multiple Cameras',
			titleKey: 'activate_storage_multi_camera_title',
			titleDefault: 'Keep the Important Moments',
			descriptionKey: 'activate_storage_multi_camera_desc',
			descriptionDefault:
				'A Camera Cloud Storage plan that grows with your home. Enjoy peace of mind with secure history, smart alerts, and AI detection.',
			ctaKey: 'button_view_camera_plans',
			ctaDefault: 'View camera plans',
		},
		'camera-upgrade': {
			tone: 'camera',
			icon: 'fas fa-arrow-up-right-dots',
			eyebrowKey: 'activate_storage_slots_full_label',
			eyebrowDefault: "You've Reached Your Camera Limit",
			titleKey: 'activate_storage_make_room_title',
			titleDefault: 'Expand Your Camera Coverage',
			descriptionKey: 'activate_storage_make_room_desc',
			descriptionDefault:
				'Upgrade your Camera Cloud Storage plan to cover more cameras, without changing your existing assignments.',
			ctaKey: 'button_upgrade_camera_plan',
			ctaDefault: 'Upgrade camera plan',
		},
		assign: {
			tone: 'ready',
			icon: 'fas fa-circle-check',
			eyebrowKey: 'activate_storage_slot_available_label',
			eyebrowDefault: 'A storage slot is available',
			titleKey: 'activate_storage_ready_title',
			titleDefault: 'Your camera is ready to start recording',
			descriptionKey: 'activate_storage_ready_desc',
			descriptionDefault:
				'Assign this camera to your available slot now. Recording begins after the assignment is saved.',
			ctaKey: 'button_assign_camera',
			ctaDefault: 'Assign camera',
		},
		manage: {
			tone: 'camera',
			icon: 'fas fa-camera',
			eyebrowKey: 'activate_storage_manage_label',
			eyebrowDefault: 'All slots are in use',
			titleKey: 'activate_storage_manage_title',
			titleDefault: 'Manage your camera assignments',
			descriptionKey: 'activate_storage_manage_desc',
			descriptionDefault:
				'Every Camera Cloud Storage slot is occupied. Open storage settings to replace a camera or free a slot.',
			ctaKey: 'modal_title_manage_camera_cloud_storage',
			ctaDefault: 'Manage Camera Cloud Storage',
		},
	};

	$: activeStepTwoContent = nextStep ? stepTwoContent[nextStep] : stepTwoContent['camera-plan'];

	// Dev-only diagnostics: the salt rides in the first 16 bytes of the blob
	// (salt[16] || iv[12] || ciphertext+tag) and iterations is a fixed constant.
	$: provisionDebugRows = provisionResult
		? [
				{
					label: $format('camera_debug_password_key', {
						default: 'Password key (PBKDF2-HMAC-SHA256, decryption key)',
					}),
					value: provisionResult.passwordKeyHex,
				},
				{
					label: $format('camera_debug_pbkdf2_salt', {
						default: 'PBKDF2 salt (base64)',
					}),
					value: bytesToBase64(base64ToBytes(provisionResult.blob).slice(0, 16)),
				},
				{
					label: $format('camera_debug_pbkdf2_iterations', {
						default: 'PBKDF2 iterations',
					}),
					value: String(PBKDF2_ITERATIONS),
				},
				{
					label: $format('camera_debug_encrypted_master_key', {
						default: 'Encrypted master key (blob)',
					}),
					value: provisionResult.blob,
				},
				{
					label: $format('camera_debug_master_key_hmac', {
						default: 'HMAC of master key',
					}),
					value: provisionResult.hmac,
				},
			]
		: [];

	const resetPassphraseFields = () => {
		passphrase = '';
		confirmPassphrase = '';
		passphraseError = '';
	};

	const clearPassphraseError = () => {
		passphraseError = '';
	};

	const closePassphraseModal = () => {
		camera_setup_prompt.close();
		resetPassphraseFields();
		currentStep = 1;
		nextStep = null;
	};

	/**
	 * Same priority chain as {@link CameraCloudStoragePromoBanner}:
	 * assigned → skip, free slots → assign, then upsell by subscription +
	 * camera count. Unassigned + Premium + CCS at max tier → manage
	 * (reassign a slot); if the CCS tier can still grow → camera-upgrade.
	 */
	const determineNextStep = (): SetupNextStep | 'complete' => {
		if (isSharedHome) return 'complete';

		const isPremium = ($user_data?.premium ?? 0) > 0;
		const hasActiveCameraPlan =
			activeCameraPlan?.status === CameraCloudStorageSubscriptionStatus.Active;

		if (hasCameraCloudStorage(deviceId)) return 'complete';
		if (availableSlotCount > 0) {
			return isCameraAssignableToCloudSlot(device) ? 'assign' : 'complete';
		}

		const cameraCount = getCameraDevices($devices ?? {}).length;
		const tier = activeCameraPlan?.tier ?? 0;

		if (!isPremium && !hasActiveCameraPlan) {
			return cameraCount <= 1 ? 'premium' : 'camera-plan';
		}
		if (isPremium && !hasActiveCameraPlan) return 'camera-plan';

		if (!isPremium && hasActiveCameraPlan) {
			if (cameraCount <= tier) return 'premium';
			if (!isHighestCameraCloudStorageTier(tier)) return 'camera-upgrade';
			return 'premium';
		}

		if (!isHighestCameraCloudStorageTier(tier)) return 'camera-upgrade';
		return isCameraAssignableToCloudSlot(device) ? 'manage' : 'complete';
	};

	const finishWithoutActivation = () => {
		camera_setup_prompt.close();
		resetPassphraseFields();
		currentStep = 1;
		nextStep = null;
		if (provisionResult) showKeyModal = true;
	};

	const navigateToSubscriptions = (cameraPlans = false) => {
		camera_setup_prompt.close();
		resetDrawerClose();
		push(
			cameraPlans
				? `/settings/premium?${CAMERA_CLOUD_STORAGE_SCROLL_PARAM}=1`
				: '/settings/premium',
		);
	};

	const assignCurrentCamera = async () => {
		if (assignmentLoading) return;
		if (!isCameraAssignableToCloudSlot(device)) return;
		const assignments = { ...$camera_cloud_storage.slotAssignments };
		const firstEmptyIndex = nextEmptySlotIndex;

		if (firstEmptyIndex === undefined) {
			toast.open({
				type: 'danger',
				title: $format('toast_camera_cloud_storage_no_free_slots', {
					default: 'No free Camera Cloud Storage slots are available.',
				}),
			});
			return;
		}

		assignmentLoading = true;
		assignments[firstEmptyIndex] = deviceId;
		const deviceIds = Object.keys(assignments)
			.map(Number)
			.sort((a, b) => a - b)
			.map((index) => assignments[index])
			.filter((id): id is string => !!id);
		const result = await setCameraSlotList(deviceIds);
		assignmentLoading = false;

		if (result.error) {
			toast.open({
				type: 'danger',
				title: $format(result.error.key, { default: result.error.default }),
			});
			return;
		}

		camera_cloud_storage.setAssignments(assignments);
		toast.open({
			type: 'success',
			title: $format('toast_camera_cloud_storage_camera_assigned', {
				default: 'Camera assigned. Cloud recordings are now active.',
			}),
		});
		finishWithoutActivation();
	};

	const handleStepTwoPrimaryAction = () => {
		if (nextStep === 'assign') {
			void assignCurrentCamera();
			return;
		}
		if (nextStep === 'manage') {
			camera_setup_prompt.close();
			resetPassphraseFields();
			currentStep = 1;
			nextStep = null;
			showManageModal = true;
			return;
		}
		navigateToSubscriptions(nextStep !== 'premium');
	};

	const showKeyErrorToast = () => {
		toast.open({
			type: 'danger',
			title: $format('camera_cloud_generate_key_error', {
				default: 'Could not generate an encryption key. Please retry.',
			}),
		});
	};

	const showKeySuccessToast = () => {
		toast.open({
			type: 'success',
			title: $format('camera_setup_success_toast', {
				default: 'Cloud recordings are set up.',
			}),
		});
	};

	const onSubmitPassphrase = async () => {
		if (provisionLoading) return;

		if (passphrase.length < MIN_PASSPHRASE_LENGTH) {
			passphraseError = $format('camera_cloud_passphrase_too_short', {
				values: { count: MIN_PASSPHRASE_LENGTH },
				default: `The password must be at least ${MIN_PASSPHRASE_LENGTH} characters.`,
			});
			return;
		}
		if (passphrase !== confirmPassphrase) {
			passphraseError = $format('camera_cloud_passphrase_mismatch', {
				default: 'The passwords do not match.',
			});
			return;
		}

		provisionLoading = true;
		try {
			provisionResult = await provisionMasterKey(device, passphrase);
			showDebug = false;
			const outcome = determineNextStep();
			if (outcome === 'complete') {
				camera_setup_prompt.close();
				resetPassphraseFields();
				showKeyModal = true;
				showKeySuccessToast();
			} else {
				nextStep = outcome;
				currentStep = 2;
				resetPassphraseFields();
			}
		} catch (error) {
			console.log('Failed to provision encryption key', error);
			showKeyErrorToast();
		}
		provisionLoading = false;
	};

	const onCopyMasterKey = () => {
		if (!provisionResult) return;
		copy(
			provisionResult.masterKey,
			$format('camera_cloud_master_key_copied', { default: 'Master key copied' }),
		);
	};

	const onCopyValue = (value: string, label: string) => {
		copy(value, `${label} copied`);
	};
</script>

<Modal
	showModal={showPassphraseModal}
	title={$format('camera_setup_modal_title', { default: 'Setup Cloud Recordings' })}
	backdropClose={false}
	on:close={closePassphraseModal}
>
	<Wizard slot="toolbar">
		<WizardItem
			active={currentStep === 1}
			disabled={currentStep !== 1}
			title={$format('setup_step_encryption', { default: 'Encryption' })}
		/>
		{#if !isSharedHome}
			<WizardItem
				active={currentStep === 2}
				disabled={currentStep !== 2}
				title={$format('setup_step_activate_storage', { default: 'Activate storage' })}
			/>
		{/if}
	</Wizard>
	<svelte:fragment slot="content">
		{#if currentStep === 1}
			<div class="d-flex justify-content-center mb-m">
				<CircleAvatar
					size="xxl"
					icon="fa-thin fa-clapperboard-play"
					borderStatus={{ icon: 'fa-solid fa-lock', color: 'color-primary' }}
				/>
			</div>
			<Text class="mb-s text-center">
				{$format('camera_setup_modal_message', {
					default:
						'Set a password to enable cloud recordings. Your media and thumbnails will be encrypted, ensuring only you can watch them.',
				})}
			</Text>
			<Text styleType="body-small" class="mb-m text-center text-muted">
				{$format('camera_setup_modal_note', {
					default:
						'No recordings or thumbnail previews are kept before setup. Your password will be required to decrypt them later - keep it safe, it cannot be recovered.',
				})}
			</Text>
			<Text styleType="body" class="mb-s">
				{$format('camera_cloud_passphrase_section_title', { default: 'Set your password' })}
			</Text>
			<form novalidate class="width-full" on:submit|preventDefault={onSubmitPassphrase}>
				<Input
					id="camera-cloud-passphrase"
					name="camera-cloud-passphrase"
					autocomplete="new-password"
					aria-label={$format('camera_cloud_passphrase_label', { default: 'Password' })}
					bind:value={passphrase}
					on:input={clearPassphraseError}
					on:enter={onSubmitPassphrase}
					size="l"
					type="password"
					class="width-full mb-s"
					label={$format('camera_cloud_passphrase_label', { default: 'Password' })}
					placeholder={$format('camera_cloud_passphrase_placeholder', {
						default: 'Enter password',
					})}
					a11yProps={{
						iconButtonAriaLabel: $format('button_show_password', {
							default: 'Show password',
						}),
					}}
				/>
				<Input
					id="camera-cloud-passphrase-confirm"
					name="camera-cloud-passphrase-confirm"
					autocomplete="new-password"
					aria-label={$format('camera_cloud_passphrase_confirm_label', {
						default: 'Confirm password',
					})}
					bind:value={confirmPassphrase}
					on:input={clearPassphraseError}
					on:enter={onSubmitPassphrase}
					error={passphraseError}
					size="l"
					type="password"
					class="width-full"
					label={$format('camera_cloud_passphrase_confirm_label', {
						default: 'Confirm password',
					})}
					placeholder={$format('camera_cloud_passphrase_confirm_placeholder', {
						default: 'Re-enter password',
					})}
					a11yProps={{
						iconButtonAriaLabel: $format('button_show_password', {
							default: 'Show password',
						}),
					}}
				/>
			</form>
		{:else if nextStep}
			<section
				class={`camera-setup-offer camera-setup-offer--${activeStepTwoContent.tone}`}
				aria-labelledby="camera-setup-offer-title"
			>
				<div class="camera-setup-offer__hero">
					<div class="camera-setup-offer__icon"><i class={activeStepTwoContent.icon}></i></div>
					<div>
						<p class="camera-setup-offer__eyebrow">
							{$format(activeStepTwoContent.eyebrowKey, {
								default: activeStepTwoContent.eyebrowDefault,
							})}
						</p>
						<h2 id="camera-setup-offer-title">
							{$format(activeStepTwoContent.titleKey, {
								default: activeStepTwoContent.titleDefault,
							})}
						</h2>
						<p>
							{$format(activeStepTwoContent.descriptionKey, {
								default: activeStepTwoContent.descriptionDefault,
							})}
						</p>
					</div>
				</div>

				{#if nextStep === 'assign'}
					<CameraSlotAssignmentPreview {device} slotIndex={nextEmptySlotIndex} />
				{:else if nextStep !== 'manage'}
					<div
						class="camera-setup-benefits"
						role="table"
						aria-label={$format('activate_storage_benefits_header', {
							default: 'What you gain',
						})}
					>
						<div class="camera-setup-benefits__header" role="row">
							<span class="text-nowrap" role="columnheader">
								{$format('activate_storage_benefits_header', {
									default: 'What you gain',
								})}
							</span>
							<span class="text-nowrap" role="columnheader">
								{$format('activate_storage_benefits_included', { default: 'Included' })}
							</span>
						</div>
						{#each benefits as benefit (benefit.titleKey)}
							<div class="camera-setup-benefit" role="row">
								<div class="camera-setup-benefit__copy" role="cell">
									<i class={benefit.icon}></i>
									<div>
										<strong>
											{$format(benefit.titleKey, {
												default: benefit.titleDefault,
											})}
										</strong>
										<span>
											{$format(benefit.descKey, { default: benefit.descDefault })}
										</span>
									</div>
								</div>
								<i class="fas fa-check camera-setup-benefit__check" role="cell"></i>
							</div>
						{/each}
					</div>
					<button
						type="button"
						class="camera-setup-benefits-link"
						on:click={() => navigateToSubscriptions(nextStep !== 'premium')}
					>
						{$format('activate_storage_see_all_benefits', { default: 'See all benefits' })}
						<i class="fas fa-arrow-right"></i>
					</button>
				{/if}
			</section>
		{/if}
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="mr-s"
			variant="secondary"
			disabled={provisionLoading || assignmentLoading}
			on:click={currentStep === 1 ? closePassphraseModal : finishWithoutActivation}
			aria-label={currentStep === 1
				? $format('button_cancel', { default: 'Cancel' })
				: $format('button_not_now', { default: 'Not now' })}
		>
			{currentStep === 1
				? $format('button_cancel', { default: 'Cancel' })
				: $format('button_not_now', { default: 'Not now' })}
		</ButtonV2>
		{#if currentStep === 1}
			<ButtonV2
				variant="primary"
				loading={provisionLoading}
				disabled={!passphrase || !confirmPassphrase || provisionLoading}
				on:click={onSubmitPassphrase}
				aria-label={$format('button_continue', { default: 'Continue' })}
			>
				{$format('button_continue', { default: 'Continue' })}
			</ButtonV2>
		{:else if nextStep}
			<ButtonV2
				class={`camera-setup-primary camera-setup-primary--${activeStepTwoContent.tone}`}
				variant={nextStep === 'premium' ? 'premium' : 'primary'}
				loading={assignmentLoading}
				disabled={assignmentLoading}
				on:click={handleStepTwoPrimaryAction}
				aria-label={$format(activeStepTwoContent.ctaKey, {
					default: activeStepTwoContent.ctaDefault,
				})}
			>
				{$format(activeStepTwoContent.ctaKey, { default: activeStepTwoContent.ctaDefault })}
			</ButtonV2>
		{/if}
	</div>
</Modal>

<Modal
	showModal={showKeyModal}
	title={$format('camera_setup_done_title', {
		default: 'Cloud recordings enabled',
	})}
	on:close={() => (showKeyModal = false)}
>
	<svelte:fragment slot="content">
		<div class="d-flex justify-content-center mb-l">
			<CircleAvatar size="xxl" icon="fas fa-shield-check text-color-success" />
		</div>
		<Text styleType="body-small" class="mb-m">
			{$format('camera_cloud_key_access_note', {
				default:
					'Your encryption is set successfully. Type your password to see camera previews and recordings.',
			})}
		</Text>
		{#if provisionResult && import.meta.env.DEV}
			<Alert
				type="warning"
				class="mb-s"
				isHeadingBold
				heading={$format('camera_debug_dev_only', { default: 'Dev-only' })}
				message={$format('camera_debug_key_warning', {
					default:
						'The encryption key below is printed in development builds only and is never shown to end users.',
				})}
			/>
			<Text styleType="label-large" class="mb-xs">
				{$format('camera_cloud_key_backup_title', { default: 'Your encryption key' })}
			</Text>
			<Text styleType="body-small" class="mb-s">
				{$format('camera_cloud_key_backup_note', {
					default:
						'Save this key somewhere safe to recover access if you forget your password.',
				})}
			</Text>
			<div class="d-flex align-items-end" style="gap: var(--spacing-xs); min-width: 0;">
				<Input
					id="camera-cloud-master-key"
					autocomplete="off"
					class="width-full"
					size="l"
					type="password"
					readonly
					value={provisionResult.masterKey}
					aria-label={$format('camera_cloud_master_key_label', { default: 'Master key' })}
					a11yProps={{
						iconButtonAriaLabel: $format('button_show_password', {
							default: 'Show password',
						}),
					}}
				/>
				<IconButton
					icon="fas fa-copy"
					on:click={onCopyMasterKey}
					aria-label={$format('button_copy_to_clipboard', {
						default: 'Copy to clipboard',
					})}
				/>
			</div>

			<div class="mt-m">
				<ButtonV2
					size="s"
					variant="secondary"
					on:click={() => (showDebug = !showDebug)}
					aria-label={$format('camera_cloud_debug_details', {
						default: 'Technical details (dev only)',
					})}
				>
					{$format('camera_cloud_debug_details', {
						default: 'Technical details (dev only)',
					})}
				</ButtonV2>
				{#if showDebug}
					<div
						class="d-flex mt-s"
						style="flex-direction: column; gap: var(--spacing-s); width: 100%;"
					>
						{#each provisionDebugRows as row (row.label)}
							<div class="width-full" style="min-width: 0;">
								<Text styleType="body-extra-small" class="text-muted">{row.label}</Text>
								<div class="d-flex align-items-center" style="min-width: 0;">
									<Text styleType="body-small" breakWord>{row.value}</Text>
									<IconButton
										class="ml-s"
										icon="fas fa-copy"
										on:click={() => onCopyValue(row.value, row.label)}
										aria-label={$format('button_copy_to_clipboard', {
											default: 'Copy to clipboard',
										})}
									/>
								</div>
							</div>
						{/each}
					</div>
				{/if}
			</div>
		{/if}
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			variant="secondary"
			on:click={() => (showKeyModal = false)}
			aria-label={$format('button_close', { default: 'Close' })}
		>
			{$format('button_close', { default: 'Close' })}
		</ButtonV2>
	</div>
</Modal>

{#if showManageModal && !isSharedHome}
	<ManageCameraCloudStorageModal
		bind:showModal={showManageModal}
		activePlan={activeCameraPlan}
		on:close={() => (showManageModal = false)}
		on:upgrade={() => {
			showManageModal = false;
			navigateToSubscriptions(true);
		}}
	/>
{/if}

<style>
	.camera-setup-offer {
		--setup-accent: #b26eff;
		--setup-accent-rgb: 178, 110, 255;
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	.camera-setup-offer--premium {
		--setup-accent: var(--color-premium);
		--setup-accent-rgb: 244, 224, 168;
	}

	.camera-setup-offer--ready {
		--setup-accent: #72dfa9;
		--setup-accent-rgb: 114, 223, 169;
	}

	.camera-setup-offer__hero {
		display: grid;
		grid-template-columns: 54px minmax(0, 1fr);
		gap: 16px;
		padding: 22px;
		background:
			radial-gradient(110% 170% at 0% 0%, rgba(var(--setup-accent-rgb), 0.2) 0%, transparent 60%),
			rgba(255, 255, 255, 0.035);
		border: 1px solid rgba(var(--setup-accent-rgb), 0.46);
		border-radius: var(--radius-big);
	}

	.camera-setup-offer__icon {
		display: grid;
		width: 54px;
		height: 54px;
		place-items: center;
		color: var(--setup-accent);
		font-size: 23px;
		background: rgba(var(--setup-accent-rgb), 0.13);
		border: 1px solid rgba(var(--setup-accent-rgb), 0.42);
		border-radius: 16px;
		box-shadow: 0 16px 32px -24px var(--setup-accent);
	}

	.camera-setup-offer__eyebrow,
	.camera-setup-offer__hero h2,
	.camera-setup-offer__hero p {
		margin: 0;
	}

	.camera-setup-offer__eyebrow {
		margin-bottom: 4px;
		color: var(--setup-accent);
		font-size: 11px;
		font-weight: 750;
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.camera-setup-offer__hero h2 {
		font-size: clamp(21px, 3vw, 28px);
		line-height: 1.2;
	}

	.camera-setup-offer__hero h2 + p {
		margin-top: 8px;
		color: rgba(255, 255, 255, 0.67);
		font-size: 14px;
		line-height: 1.5;
	}

	.camera-setup-benefits {
		overflow: hidden;
		background: rgba(255, 255, 255, 0.025);
		border: 1px solid rgba(255, 255, 255, 0.1);
		border-radius: var(--radius-big);
	}

	.camera-setup-benefits__header,
	.camera-setup-benefit {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(82px, max-content);
		align-items: center;
	}

	.camera-setup-benefits__header {
		padding: 10px 16px;
		color: rgba(255, 255, 255, 0.46);
		font-size: 10px;
		font-weight: 750;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		background: rgba(255, 255, 255, 0.035);
	}

	.camera-setup-benefits__header span {
		white-space: nowrap;
	}

	.camera-setup-benefits__header span:last-child {
		padding-left: 12px;
		text-align: center;
	}

	.camera-setup-benefit {
		min-height: 66px;
		padding: 12px 16px;
		border-top: 1px solid rgba(255, 255, 255, 0.08);
		box-sizing: border-box;
	}

	.camera-setup-benefit__copy {
		display: grid;
		grid-template-columns: 26px minmax(0, 1fr);
		align-items: center;
		gap: 11px;
	}

	.camera-setup-benefit__copy > i {
		color: var(--setup-accent);
		font-size: 16px;
		text-align: center;
	}

	.camera-setup-benefit__copy div {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.camera-setup-benefit__copy strong {
		font-size: 14px;
	}

	.camera-setup-benefit__copy span {
		color: rgba(255, 255, 255, 0.55);
		font-size: 12px;
		line-height: 1.35;
	}

	.camera-setup-benefit__check {
		display: grid;
		width: 100%;
		place-items: center;
		justify-self: stretch;
		color: var(--setup-accent);
	}

	.camera-setup-benefits-link {
		display: inline-flex;
		align-items: center;
		align-self: flex-start;
		gap: 7px;
		margin: -6px 0 0;
		padding: 0;
		color: var(--setup-accent);
		font: inherit;
		font-size: 13px;
		font-weight: 650;
		background: transparent;
		border: 0;
		cursor: pointer;
		transition:
			color 0.2s ease,
			gap 0.2s ease;
	}

	.camera-setup-benefits-link:hover,
	.camera-setup-benefits-link:focus-visible {
		gap: 10px;
		color: #ffffff;
	}

	:global(.camera-setup-primary--camera),
	:global(.camera-setup-primary--camera:hover) {
		color: #ffffff !important;
		background: linear-gradient(110deg, #d957f2 0%, #893bf2 100%) !important;
		border-color: transparent !important;
		box-shadow: 0 12px 30px -16px rgba(178, 110, 255, 0.9) !important;
	}

	:global(.camera-setup-primary--ready),
	:global(.camera-setup-primary--ready:hover) {
		color: #171717 !important;
		background: #f4f4f4 !important;
		border-color: transparent !important;
		box-shadow: 0 10px 24px -18px rgba(255, 255, 255, 0.55) !important;
	}

	@media (max-width: 620px) {
		.camera-setup-offer__hero {
			grid-template-columns: 44px minmax(0, 1fr);
			padding: 18px;
		}

		.camera-setup-offer__icon {
			width: 44px;
			height: 44px;
			font-size: 19px;
			border-radius: 13px;
		}
	}
</style>
